import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from '../../../components/ui/dialog';
import { patientsApi, scansApi, generationApi } from '../../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  UserRound,
  Phone,
  Calendar,
  Plus,
  Loader2,
  ScanLine,
  Image,
  Activity,
  Clock,
  Edit,
  Trash2,
  Sparkles,
  Camera,
  Check,
  ArrowRight,
  RefreshCw,
  Pencil,
  MessageCircle,
  QrCode
} from 'lucide-react';
import { ShareActions } from '../../../components/shared/ShareActions';

export const PatientDetailPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scansLoading, setScansLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewScanModalOpen, setIsNewScanModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    pregnancy_week: '',
    notes: ''
  });

  // Analiz oluşturma aşaması (1: OCR, 2: biyometri, 3: analiz tipi, 4: görüntüler)
  const [analysisStep, setAnalysisStep] = useState(1);
  
  // OCR state
  const ocrFileRef = useRef(null);
  const ocrFileRef2 = useRef(null);
  const profileFileRef = useRef(null);
  const frontalFileRef = useRef(null);
  const [ocrImage1, setOcrImage1] = useState(null); // El yazısı
  const [ocrImage2, setOcrImage2] = useState(null); // Cihaz ekranı
  const [ocrLoading, setOcrLoading] = useState(false);
  
  // Ultrason görüntüleri (4K için)
  const [profileImage, setProfileImage] = useState(null);
  const [frontalImage, setFrontalImage] = useState(null);
  
  // 4K Generation (inline wizard)
  const [generationStatus, setGenerationStatus] = useState('idle');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generationJobId, setGenerationJobId] = useState(null);
  const [generationProgress, setGenerationProgress] = useState('');
  const [lastScanId, setLastScanId] = useState(null);
  
  // TAM BİYOMETRİK ÖLÇÜMLER - Tüm analizler için ortak
  const [biometrics, setBiometrics] = useState({
    // Hadlock Parametreleri
    bpd: '',      // Biparietal Diameter
    ofd: '',      // Occipitofrontal Diameter (Hansmann)
    hc: '',       // Head Circumference
    ac: '',       // Abdominal Circumference
    fl: '',       // Femur Length
    
    // Jeanty Parametreleri (Kemik Uzunlukları)
    tib: '',      // Tibia
    fib: '',      // Fibula
    hl: '',       // Humerus Length
    rad: '',      // Radius
    ulna: '',     // Ulna
    
    // Beyin Parametreleri
    cereb: '',    // Cerebellum (Hill)
    cm: '',       // Cisterna Magna
    nf: '',       // Nuchal Fold
    
    // Yüz Parametreleri
    fromen: '',       // Alın yüksekliği
    burun: '',        // Burun uzunluğu
    goztepe: '',      // Göz arası mesafe (IOD)
    bioc_cap: '',     // Yüz genişliği
    cene: '',         // Çene uzunluğu
    agiz_capi: '',    // Ağız genişliği
    onarka_bas: '',   // Ön-arka baş
    goz: '',          // Göz boyutu
    ust_dudak: '',    // Üst dudak
    
    // Diğer
    cinsiyet: ''      // Cinsiyet (K/E)
  });

  // New scan state
  const [scanInputType, setScanInputType] = useState('ultrasound');
  // "Yeni Analiz" → ilk açılışta iki seçenek (Ultrason 4K wizard / AI Yüz Analizi)
  // gösterilir. Doktor seçim yapınca ilgili akışa geçilir.
  const [analysisPicker, setAnalysisPicker] = useState(true);

  const fetchPatient = useCallback(async () => {
    try {
      setLoading(true);
      const data = await patientsApi.get(patientId);
      setPatient(data);
      setEditFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        phone: data.phone || '',
        pregnancy_week: data.pregnancy_week?.toString() || '',
        notes: data.notes || ''
      });
      // Biyometrik verileri de yükle
      if (data.biometrics) {
        setBiometrics({
          bpd: data.biometrics.bpd || '',
          hc: data.biometrics.hc || '',
          ac: data.biometrics.ac || '',
          fl: data.biometrics.fl || '',
          ofd: data.biometrics.ofd || '',
          nasal_bone: data.biometrics.nasal_bone || '',
          iod: data.biometrics.iod || ''
        });
      }
    } catch (error) {
      toast.error('Hasta bilgileri yüklenirken hata oluştu');
      navigate('/doctor/patients');
    } finally {
      setLoading(false);
    }
  }, [patientId, navigate]);

  const fetchScans = useCallback(async () => {
    try {
      setScansLoading(true);
      const data = await scansApi.getByPatient(patientId);
      setScans(data);
    } catch (error) {
      console.error('Scans error:', error);
    } finally {
      setScansLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchPatient();
    fetchScans();
  }, [fetchPatient, fetchScans]);

  const handleUpdatePatient = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        phone: editFormData.phone || null,
        pregnancy_week: editFormData.pregnancy_week ? parseInt(editFormData.pregnancy_week) : null,
        notes: editFormData.notes || null,
        biometrics: biometrics // Biyometrik verileri de kaydet
      };

      await patientsApi.update(patientId, payload);
      toast.success('Hasta bilgileri güncellendi');
      setIsEditModalOpen(false);
      fetchPatient();
    } catch (error) {
      toast.error(error.message || 'Güncelleme hatası');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePatient = async () => {
    if (!window.confirm('Bu hastayı silmek istediğinizden emin misiniz?')) return;

    try {
      await patientsApi.delete(patientId);
      toast.success('Hasta silindi');
      navigate('/doctor/patients');
    } catch (error) {
      toast.error(error.message || 'Silme hatası');
    }
  };

  const handleCreateScan = async (typeOverride = null) => {
    setSubmitting(true);
    try {
      let engine = 'mock';
      // typeOverride: picker'dan direkt geliyorsa React state'in commit
      // olmasını beklemeden o değeri kullan (setState async).
      let inputType = typeOverride || scanInputType;
      const activeType = typeOverride || scanInputType;
      
      if (activeType === 'biometric') {
        engine = 'live_editor';
      } else if (activeType === 'ai_face') {
        engine = 'ai_face';
        inputType = 'ultrasound';
      } else if (activeType === 'ultrasound') {
        engine = 'ultrasound';
      } else if (activeType === 'ultrasound_4k') {
        engine = 'ultrasound_4k';
        inputType = 'ultrasound_4k';
      }
      
      // 4K ise görüntülerle birlikte scan oluştur ve üretim başlat
      if (activeType === 'ultrasound_4k' && profileImage && frontalImage) {
        // ÖNCE biyometrileri map'le
        const uiToBackendMap = {
          burun: 'nasal_bone', goztepe: 'iod', goz: 'eye_diameter',
          agiz_capi: 'mouth_diameter', ust_dudak: 'upper_lip', cene: 'chin'
        };
        const bioForGen = {};
        Object.entries(biometrics).forEach(([k, v]) => {
          if (v !== '' && v !== undefined && k !== 'cinsiyet') {
            const backendKey = uiToBackendMap[k] || k;
            bioForGen[backendKey] = parseFloat(v) || 0;
          }
        });
        
        setGenerationProgress('Scan olusturuluyor...');
        setGenerationStatus('generating');
        
        // Scan olustur
        let scan;
        try {
          scan = await scansApi.create({
            patient_id: patientId,
            input_type: inputType,
            engine: engine,
            metadata: {
              biometrics: bioForGen,
              gender: biometrics.cinsiyet === 'K' ? 'female' : 'male',
              source: 'quick_exam'
            }
          });
        } catch (scanErr) {
          setGenerationStatus('failed');
          setGenerationProgress('Scan olusturulamadi: ' + (scanErr.message || 'Bilinmeyen hata'));
          toast.error('Scan olusturulamadi: ' + (scanErr.message || ''));
          return;
        }
        
        setGenerationProgress('Goruntuler kaydediliyor...');
        
        // Save only lightweight metadata (images passed directly to generation API)
        try {
          await scansApi.update(scan.id, {
            metadata: {
              biometrics: bioForGen,
              gender: biometrics.cinsiyet === 'K' ? 'female' : 'male',
            }
          });
        } catch (updateErr) {
          console.warn('Scan metadata update hatasi:', updateErr);
        }
        
        setGenerationProgress('4K uretim baslatiliyor...');
        
        // 4K üretim başlat
        let startResult;
        try {
          startResult = await generationApi.startScientificUltrasound4K({
            image: profileImage,
            patient_id: patientId,
            scan_id: scan.id,
            side_image: profileImage,
            front_image: frontalImage,
            biometrics: bioForGen,
            pregnancy_week: parseInt(patient?.pregnancy_week) || 24,
            gender: biometrics.cinsiyet === 'K' ? 'female' : 'male',
            view_type: 'combined',
          });
        } catch (genErr) {
          setGenerationStatus('failed');
          setGenerationProgress('Uretim baslatilamadi: ' + (genErr.message || 'Sunucu hatasi'));
          toast.error('Uretim baslatilamadi: ' + (genErr.message || ''));
          return;
        }
        
        toast.success('4K uretim baslatildi!');
        setGenerationJobId(startResult.job_id);
        setLastScanId(scan.id);
        setGenerationProgress('AI goruntuyu isliyor...');
        return;
      }
      
      const scan = await scansApi.create({
        patient_id: patientId,
        input_type: inputType,
        engine: engine,
        biometrics: biometrics,
        pregnancy_week: patient?.pregnancy_week || null
      });
      toast.success('Yeni analiz olusturuldu');
      setIsNewScanModalOpen(false);
      
      if (activeType === 'ai_face') {
        navigate(`/doctor/ai-face?scanId=${scan.id}`);
      } else if (activeType === 'ultrasound_4k') {
        navigate(`/doctor/ultrasound-4k?scanId=${scan.id}`);
      } else {
        navigate(`/doctor/editor?scanId=${scan.id}`);
      }
    } catch (error) {
      toast.error(error.message || 'Analiz olusturulurken hata olustu');
      if (generationStatus === 'generating') {
        setGenerationStatus('failed');
        setGenerationProgress(error.message || 'Beklenmeyen hata olustu');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Resim boyutunu küçült (iOS Safari uyumu)
  const resizeImage = useCallback((dataUrl, maxWidth = 2000) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.90));
      };
      img.src = dataUrl;
    });
  }, []);

  // OCR fotoğraf çekimi
  const handleOcrFileSelect = useCallback(async (e, targetSlot) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const resized = await resizeImage(ev.target.result);
      if (targetSlot === 1) setOcrImage1(resized);
      else setOcrImage2(resized);
      
      // OCR çalıştır
      setOcrLoading(true);
      try {
        const result = await generationApi.extractBiometrics(resized);
        if (!result.success) {
          toast.error('OCR basarisiz: ' + (result.error || 'Goruntu okunamadi'));
          setOcrLoading(false);
          return;
        }
        const fields = ['bpd','hc','ac','fl','ofd','fromen','nasal_bone','iod','eye','bioc_cap','mouth_diameter','upper_lip','chin'];
        const keyMap = { nasal_bone: 'burun', iod: 'goztepe', eye: 'goz', mouth_diameter: 'agiz_capi', upper_lip: 'ust_dudak', chin: 'cene' };
        let filledCount = 0;
        setBiometrics(prev => {
          const updated = { ...prev };
          fields.forEach(f => {
            if (result[f] !== null && result[f] !== undefined) {
              const uiKey = keyMap[f] || f;
              updated[uiKey] = String(result[f]);
              filledCount++;
            }
          });
          return updated;
        });
        if (filledCount === 0) {
          toast.warning('Goruntude olcum degeri bulunamadi. Lutfen net bir fotograf cekin veya degerleri elle girin.');
        } else {
          toast.success(targetSlot === 1 ? `El yazisi: ${filledCount} olcum okundu` : `Cihaz: ${filledCount} olcum okundu`);
        }
      } catch (err) {
        const msg = err.message || '';
        if (msg.includes('503') || msg.includes('UNAVAILABLE')) {
          toast.error('AI servisi su an yogun. Lutfen 30 saniye bekleyip tekrar deneyin.');
        } else {
          toast.error('OCR hatasi: ' + msg);
        }
      } finally {
        setOcrLoading(false);
      }
    };
    reader.readAsDataURL(file);
  }, [resizeImage]);

  // Profil ultrason cekimi (with validation)
  const handleProfileCapture = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const resized = await resizeImage(ev.target.result);
      setProfileImage(resized);
      toast.success('Profil goruntu kaydedildi');
      // Validate view type
      try {
        const validation = await generationApi.validateUltrasoundImage(resized);
        const view = (validation.face_view || '').toLowerCase();
        if (view.includes('front') || view === 'frontal') {
          toast.warning('Bu goruntu frontal gorunuyor. Profil slot icin yan goruntuyu cekin.');
        }
      } catch (_) { /* validation optional */ }
    };
    reader.readAsDataURL(file);
  }, [resizeImage]);

  // Frontal ultrason cekimi (with validation)
  const handleFrontalCapture = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const resized = await resizeImage(ev.target.result);
      setFrontalImage(resized);
      toast.success('Frontal goruntu kaydedildi');
      // Validate view type
      try {
        const validation = await generationApi.validateUltrasoundImage(resized);
        const view = (validation.face_view || '').toLowerCase();
        if (view.includes('side') || view.includes('profil') || view === 'profile') {
          toast.warning('Bu goruntu profil gorunuyor. Frontal slot icin on yuz goruntuyu cekin.');
        }
      } catch (_) { /* validation optional */ }
    };
    reader.readAsDataURL(file);
  }, [resizeImage]);

  // Modal kapanınca sıfırla
  const resetModal = () => {
    setAnalysisStep(1);
    setOcrImage1(null);
    setOcrImage2(null);
    setProfileImage(null);
    setFrontalImage(null);
    setScanInputType('ultrasound_4k');
    setGenerationStatus('idle');
    setGeneratedImage(null);
    setGenerationJobId(null);
    setGenerationProgress('');
    setLastScanId(null);
    setAnalysisPicker(true);
  };

  // 4K generation polling (inline in wizard) - Safari/iPad compatible
  const generationJobIdRef = useRef(null);
  const lastScanIdRef = useRef(null);
  
  // Keep refs in sync with state
  useEffect(() => { generationJobIdRef.current = generationJobId; }, [generationJobId]);
  useEffect(() => { lastScanIdRef.current = lastScanId; }, [lastScanId]);

  // Single check function - reusable
  const checkGenerationStatus = useCallback(async () => {
    const jobId = generationJobIdRef.current;
    if (!jobId) return null;
    try {
      const status = await generationApi.getScientificUltrasound4KStatus(jobId);
      if (status.status === 'completed') {
        setGeneratedImage(status.enhanced_image);
        setGenerationStatus('completed');
        const scanId = lastScanIdRef.current;
        if (scanId) {
          try { await scansApi.update(scanId, { status: 'generated' }); } catch (_) {}
        }
        toast.success('4K uretim tamamlandi!');
        fetchScans();
        return 'completed';
      } else if (status.status === 'failed') {
        setGenerationStatus('failed');
        setGenerationProgress(status.error || 'Uretim basarisiz');
        toast.error(status.error || 'Uretim basarisiz');
        return 'failed';
      }
      setGenerationProgress(status.progress || 'Isleniyor...');
      return 'processing';
    } catch (e) {
      return 'error';
    }
  }, [fetchScans]);

  useEffect(() => {
    if (!generationJobId || generationStatus !== 'generating') return;
    let cancelled = false;
    let consecutiveErrors = 0;
    const startTime = Date.now();
    const maxDuration = 5 * 60 * 1000; // 5 dakika
    
    const poll = async () => {
      while (!cancelled) {
        await new Promise(r => setTimeout(r, 3000));
        if (cancelled) break;
        
        // Zaman kontrolü
        if (Date.now() - startTime > maxDuration) {
          // Son bir kez daha kontrol et - belki tamamlanmıştır
          const finalCheck = await checkGenerationStatus();
          if (finalCheck === 'completed' || finalCheck === 'failed') break;
          setGenerationStatus('failed');
          setGenerationProgress('Uretim zaman asimina ugradi. Tekrar Dene butonuna basin.');
          toast.error('Uretim zaman asimina ugradi');
          break;
        }
        
        const result = await checkGenerationStatus();
        if (result === 'completed' || result === 'failed') break;
        if (result === 'error') {
          consecutiveErrors++;
          if (consecutiveErrors > 8) {
            setGenerationStatus('failed');
            setGenerationProgress('Sunucu baglantisi kayboldu. Tekrar deneyin.');
            toast.error('Sunucu baglantisi kayboldu');
            break;
          }
        } else {
          consecutiveErrors = 0;
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [generationJobId, generationStatus, checkGenerationStatus]);
  
  // Safari/iPad: Tab tekrar görünür olduğunda durumu kontrol et
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible' && generationStatus === 'generating' && generationJobId) {
        const result = await checkGenerationStatus();
        if (result === 'completed') {
          toast.success('4K goruntu hazir!');
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [generationStatus, generationJobId, checkGenerationStatus]);

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Taslak', className: 'bg-slate-100 text-slate-600' },
      generated: { label: 'Tamamlandı', className: 'bg-green-100 text-green-600' },
      failed: { label: 'Başarısız', className: 'bg-red-100 text-red-600' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Hasta Detayı">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!patient) {
    return (
      <DashboardLayout title="Hasta Bulunamadı">
        <div className="text-center py-12">
          <p className="text-slate-500">Hasta kaydı bulunamadı.</p>
          <Button onClick={() => navigate('/doctor/patients')} className="mt-4">
            Hastalar Sayfasına Dön
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={`${patient.first_name} ${patient.last_name}`}>
      <div className="space-y-6" data-testid="patient-detail-page">
        {/* Back button and actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/doctor/patients')}
            className="text-slate-600 hover:text-slate-900"
            data-testid="back-to-patients"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Hastalara Dön
          </Button>

          <div className="flex items-center gap-2">
            <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="edit-patient-button">
                  <Edit className="w-4 h-4 mr-2" />
                  Düzenle
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Hasta Bilgilerini Düzenle</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleUpdatePatient} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_first_name">Ad *</Label>
                      <Input
                        id="edit_first_name"
                        name="first_name"
                        value={editFormData.first_name}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_last_name">Soyad *</Label>
                      <Input
                        id="edit_last_name"
                        name="last_name"
                        value={editFormData.last_name}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit_phone">Telefon</Label>
                      <Input
                        id="edit_phone"
                        name="phone"
                        type="tel"
                        value={editFormData.phone}
                        onChange={handleEditInputChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_pregnancy_week">Gebelik Haftası</Label>
                      <Input
                        id="edit_pregnancy_week"
                        name="pregnancy_week"
                        type="number"
                        min="1"
                        max="42"
                        value={editFormData.pregnancy_week}
                        onChange={handleEditInputChange}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="edit_notes">Notlar</Label>
                    <textarea
                      id="edit_notes"
                      name="notes"
                      value={editFormData.notes}
                      onChange={handleEditInputChange}
                      rows={3}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>

                  <DialogFooter>
                    <DialogClose asChild>
                      <Button type="button" variant="outline">İptal</Button>
                    </DialogClose>
                    <Button type="submit" className="bg-[#5A67D8] hover:bg-[#4F46E5]" disabled={submitting}>
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Kaydet
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Button
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleDeletePatient}
              data-testid="delete-patient-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Sil
            </Button>
          </div>
        </div>

        {/* Patient Info Card — Bilgiler direkt burada */}
        <Card className="border-0 shadow-lg" data-testid="patient-info-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-[#E0D7FF] flex items-center justify-center flex-shrink-0">
                <UserRound className="w-6 h-6 md:w-8 md:h-8 text-[#5A67D8]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-2xl font-bold text-slate-800">
                  {patient.first_name} {patient.last_name}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mt-2 md:mt-3">
                  <div>
                    <p className="text-xs text-slate-400">Telefon</p>
                    <p className="text-sm font-medium text-slate-700 flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5" />
                      {patient.phone || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Gebelik Haftası</p>
                    <p className="text-sm font-medium text-slate-700 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {patient.pregnancy_week ? `${patient.pregnancy_week}. hafta` : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400">Kayıt Tarihi</p>
                    <p className="text-sm font-medium text-slate-700">
                      {new Date(patient.created_at).toLocaleDateString('tr-TR')}
                    </p>
                  </div>
                  {patient.notes && (
                    <div>
                      <p className="text-xs text-slate-400">Not</p>
                      <p className="text-sm text-slate-700 truncate">{patient.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Analizler — Tab olmadan direkt göster */}
        <div className="space-y-6">
          <Card className="border-0 shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ScanLine className="w-5 h-5 text-[#5A67D8]" />
                  Analizler
                </CardTitle>

                <Dialog open={isNewScanModalOpen} onOpenChange={(open) => {
                  setIsNewScanModalOpen(open);
                  if (!open) resetModal();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#5A67D8] hover:bg-[#4F46E5]" data-testid="new-scan-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Yeni Analiz
                    </Button>
                  </DialogTrigger>
                                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-amber-500" />
                        {analysisPicker ? 'Analiz Türü Seçin' : 'Hizli Muayene'}
                      </DialogTitle>
                      <DialogDescription className="text-sm text-slate-500">
                        {patient?.first_name} {patient?.last_name} — {patient?.pregnancy_week || '-'}. hafta
                      </DialogDescription>
                    </DialogHeader>

                    {/* ─── İLK EKRAN: ANALİZ TÜRÜ SEÇİMİ ─── */}
                    {analysisPicker ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4" data-testid="analysis-picker">
                        <button
                          type="button"
                          onClick={() => { setScanInputType('ultrasound_4k'); setAnalysisPicker(false); }}
                          className="p-5 rounded-2xl border-2 border-slate-200 hover:border-amber-400 hover:bg-amber-50/50 transition-all text-left group"
                          data-testid="picker-ultrasound-4k"
                        >
                          <Sparkles className="w-8 h-8 text-amber-500 mb-2 group-hover:scale-110 transition-transform" />
                          <p className="font-semibold text-slate-900">Ultrason 4K</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Profil + frontal ultrason kesitlerinden hızlı muayene wizard'ı ile 4K bebek yüzü
                          </p>
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            // AI Yüz Analizi: setState async olduğu için
                            // değeri doğrudan handleCreateScan'e geçiriyoruz —
                            // aksi halde scanInputType eski değerde kalıp
                            // /doctor/editor rotasına gidiyordu.
                            setScanInputType('ai_face');
                            setAnalysisPicker(false);
                            await handleCreateScan('ai_face');
                          }}
                          className="p-5 rounded-2xl border-2 border-slate-200 hover:border-pink-400 hover:bg-pink-50/50 transition-all text-left group"
                          data-testid="picker-ai-face"
                        >
                          <ScanLine className="w-8 h-8 text-pink-500 mb-2 group-hover:scale-110 transition-transform" />
                          <p className="font-semibold text-slate-900">AI Yüz Analizi</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Anne + baba fotoğrafıyla genetik füzyon — benzersiz bebek yüzü üretimi
                          </p>
                        </button>
                      </div>
                    ) : (
                    <>
                    {/* 6-Step Progress Bar */}
                    <div className="flex items-center gap-0.5 py-2 overflow-x-auto" data-testid="wizard-steps">
                      {[
                        { num: 1, label: 'El Yazisi', icon: Pencil },
                        { num: 2, label: 'Cihaz', icon: ScanLine },
                        { num: 3, label: 'Olcumler', icon: Activity },
                        { num: 4, label: 'Profil', icon: Image },
                        { num: 5, label: 'Frontal', icon: Image },
                        { num: 6, label: '4K Uret', icon: Sparkles },
                      ].map((s, i) => (
                        <React.Fragment key={s.num}>
                          <button
                            type="button"
                            onClick={() => s.num <= analysisStep && generationStatus === 'idle' && setAnalysisStep(s.num)}
                            className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap ${
                              analysisStep === s.num ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                              s.num < analysisStep ? 'bg-green-50 text-green-700 border border-green-200' :
                              'bg-slate-50 text-slate-400 border border-slate-200'
                            }`}
                            data-testid={`wizard-step-${s.num}`}
                          >
                            {s.num < analysisStep ? <Check className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
                            <span className="hidden sm:inline">{s.label}</span>
                            <span className="sm:hidden">{s.num}</span>
                          </button>
                          {i < 5 && <ArrowRight className={`w-3 h-3 flex-shrink-0 ${s.num < analysisStep ? 'text-green-400' : 'text-slate-300'}`} />}
                        </React.Fragment>
                      ))}
                    </div>

                    {/* Hidden file inputs for iOS native camera */}
                    <input ref={ocrFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleOcrFileSelect(e, 1)} data-testid="ocr-input-1" />
                    <input ref={ocrFileRef2} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleOcrFileSelect(e, 2)} data-testid="ocr-input-2" />
                    <input ref={profileFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleProfileCapture} data-testid="profile-input" />
                    <input ref={frontalFileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFrontalCapture} data-testid="frontal-input" />

                    {/* ADIM 1: EL YAZISI OCR */}
                    {analysisStep === 1 && (
                      <div className="space-y-4 mt-2" data-testid="step-ocr-handwriting">
                        <p className="text-xs text-slate-500">El yazisi olcum kagidini fotograflayin. Atlamak icin Devam'a basin.</p>
                        <div className={`border-2 ${ocrImage1 ? 'border-green-300 bg-green-50/30' : 'border-dashed border-slate-200'} rounded-xl p-6 text-center space-y-3`}>
                          <Pencil className="w-10 h-10 mx-auto text-slate-400" />
                          <p className="text-base font-medium text-slate-700">El Yazisi Olcumler</p>
                          <p className="text-xs text-slate-500">Olcum kagidini fotograflayin</p>
                          {ocrImage1 ? (
                            <div className="space-y-2">
                              <img src={ocrImage1} alt="El yazisi" className="w-full max-h-48 object-contain rounded-lg" />
                              <Button size="sm" variant="outline" onClick={() => { setOcrImage1(null); ocrFileRef.current?.click(); }} data-testid="retake-ocr1">
                                <RefreshCw className="w-3 h-3 mr-1" /> Tekrar Cek
                              </Button>
                            </div>
                          ) : (
                            <Button onClick={() => ocrFileRef.current?.click()} className="bg-amber-500 hover:bg-amber-600" size="lg" data-testid="capture-ocr1">
                              <Camera className="w-5 h-5 mr-2" /> Fotograf Cek
                            </Button>
                          )}
                        </div>
                        {ocrLoading && (
                          <div className="flex items-center justify-center gap-2 py-3 bg-amber-50 rounded-lg">
                            <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                            <span className="text-sm text-amber-700">AI olcumleri okuyor...</span>
                          </div>
                        )}
                        {!ocrLoading && ocrImage1 && (
                          <div className="bg-green-50 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2">
                            <Check className="w-4 h-4" /> El yazisi olcumleri okundu
                          </div>
                        )}
                      </div>
                    )}

                    {/* ADIM 2: CIHAZ EKRANI OCR */}
                    {analysisStep === 2 && (
                      <div className="space-y-4 mt-2" data-testid="step-ocr-device">
                        <p className="text-xs text-slate-500">Ultrason cihaz ekranini fotograflayin. Atlamak icin Devam'a basin.</p>
                        <div className={`border-2 ${ocrImage2 ? 'border-green-300 bg-green-50/30' : 'border-dashed border-slate-200'} rounded-xl p-6 text-center space-y-3`}>
                          <ScanLine className="w-10 h-10 mx-auto text-slate-400" />
                          <p className="text-base font-medium text-slate-700">Cihaz Ekrani</p>
                          <p className="text-xs text-slate-500">Cihaz ekranindaki olcumleri fotograflayin</p>
                          {ocrImage2 ? (
                            <div className="space-y-2">
                              <img src={ocrImage2} alt="Cihaz ekrani" className="w-full max-h-48 object-contain rounded-lg" />
                              <Button size="sm" variant="outline" onClick={() => { setOcrImage2(null); ocrFileRef2.current?.click(); }} data-testid="retake-ocr2">
                                <RefreshCw className="w-3 h-3 mr-1" /> Tekrar Cek
                              </Button>
                            </div>
                          ) : (
                            <Button onClick={() => ocrFileRef2.current?.click()} className="bg-amber-500 hover:bg-amber-600" size="lg" data-testid="capture-ocr2">
                              <Camera className="w-5 h-5 mr-2" /> Fotograf Cek
                            </Button>
                          )}
                        </div>
                        {ocrLoading && (
                          <div className="flex items-center justify-center gap-2 py-3 bg-amber-50 rounded-lg">
                            <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                            <span className="text-sm text-amber-700">AI olcumleri okuyor...</span>
                          </div>
                        )}
                        {!ocrLoading && ocrImage2 && (
                          <div className="bg-green-50 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2">
                            <Check className="w-4 h-4" /> Cihaz olcumleri okundu
                          </div>
                        )}
                      </div>
                    )}

                    {/* ADIM 3: BIYOMETRIK OLCUMLER */}
                    {analysisStep === 3 && (
                      <div className="space-y-3 mt-2" data-testid="step-biometrics">
                        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-blue-900 flex items-center gap-2">
                              Biyometrik Ölçümler
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium" data-testid="biometrics-optional-badge">OPSİYONEL</span>
                            </p>
                            <p className="text-xs text-blue-700 mt-0.5">İsterseniz boş bırakıp <span className="font-medium">"Atla"</span> ile geçebilirsiniz. Bu adım 4K üretimini engellemez.</p>
                          </div>
                        </div>
                        {/* Hadlock */}
                        <div className="border rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-slate-600 mb-2">Hadlock (mm)</h4>
                          <div className="grid grid-cols-5 gap-2">
                            {[['bpd','BPD'],['ofd','OFD'],['hc','HC'],['ac','AC'],['fl','FL']].map(([k,l]) => (
                              <div key={k}>
                                <Label className="text-[10px]">{l}</Label>
                                <Input type="number" value={biometrics[k]} onChange={(e) => setBiometrics({...biometrics, [k]: e.target.value})} className="h-7 text-xs" />
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Jeanty */}
                        <div className="border rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-slate-600 mb-2">Jeanty - Kemik (mm)</h4>
                          <div className="grid grid-cols-5 gap-2">
                            {[['tib','TIB'],['fib','FIB'],['hl','HL'],['rad','RAD'],['ulna','Ulna']].map(([k,l]) => (
                              <div key={k}>
                                <Label className="text-[10px]">{l}</Label>
                                <Input type="number" value={biometrics[k]} onChange={(e) => setBiometrics({...biometrics, [k]: e.target.value})} className="h-7 text-xs" />
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Beyin */}
                        <div className="border rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-slate-600 mb-2">Beyin (mm)</h4>
                          <div className="grid grid-cols-3 gap-2">
                            {[['cereb','Cereb'],['cm','CM'],['nf','NF']].map(([k,l]) => (
                              <div key={k}>
                                <Label className="text-[10px]">{l}</Label>
                                <Input type="number" value={biometrics[k]} onChange={(e) => setBiometrics({...biometrics, [k]: e.target.value})} className="h-7 text-xs" />
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Yuz */}
                        <div className="border rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-slate-600 mb-2">Yuz (mm)</h4>
                          <div className="grid grid-cols-4 gap-2">
                            {[['fromen','Fromen'],['burun','Burun'],['goztepe','Göztepe'],['bioc_cap','BiocÇap'],['cene','Çene'],['agiz_capi','Ağızçapı'],['goz','Göz'],['ust_dudak','Üst Dudak']].map(([k,l]) => (
                              <div key={k}>
                                <Label className="text-[10px]">{l}</Label>
                                <Input type="number" value={biometrics[k]} onChange={(e) => setBiometrics({...biometrics, [k]: e.target.value})} className="h-7 text-xs" />
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Diger */}
                        <div className="border rounded-lg p-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[10px]">Önarka baş</Label>
                              <Input type="number" value={biometrics.onarka_bas} onChange={(e) => setBiometrics({...biometrics, onarka_bas: e.target.value})} className="h-7 text-xs" />
                            </div>
                            <div>
                              <Label className="text-[10px]">Cinsiyet</Label>
                              <select value={biometrics.cinsiyet} onChange={(e) => setBiometrics({...biometrics, cinsiyet: e.target.value})} className="w-full h-7 text-xs border rounded-md px-2">
                                <option value="">Seciniz</option>
                                <option value="K">Kiz</option>
                                <option value="E">Erkek</option>
                                <option value="B">Belirsiz</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ADIM 4: PROFIL ULTRASON */}
                    {analysisStep === 4 && (
                      <div className="space-y-4 mt-2" data-testid="step-profile">
                        <p className="text-xs text-slate-500">Bebegin yan profil ultrason goruntusunu cekin.</p>
                        <div className={`border-2 ${profileImage ? 'border-green-300 bg-green-50/30' : 'border-dashed border-slate-200'} rounded-xl p-6 text-center space-y-3`}>
                          <Image className="w-10 h-10 mx-auto text-blue-400" />
                          <p className="text-base font-medium text-slate-700">Profil Ultrason</p>
                          <p className="text-xs text-slate-500">Yan profil goruntuyu cekin</p>
                          {profileImage ? (
                            <div className="space-y-2">
                              <img src={profileImage} alt="Profil" className="w-full max-h-52 object-contain rounded-lg" />
                              <Button size="sm" variant="outline" onClick={() => { setProfileImage(null); profileFileRef.current?.click(); }} data-testid="retake-profile">
                                <RefreshCw className="w-3 h-3 mr-1" /> Tekrar Cek
                              </Button>
                            </div>
                          ) : (
                            <Button onClick={() => profileFileRef.current?.click()} className="bg-[#5A67D8] hover:bg-[#4F46E5]" size="lg" data-testid="capture-profile">
                              <Camera className="w-5 h-5 mr-2" /> Fotograf Cek
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ADIM 5: FRONTAL ULTRASON */}
                    {analysisStep === 5 && (
                      <div className="space-y-4 mt-2" data-testid="step-frontal">
                        <p className="text-xs text-slate-500">Bebegin on yuz (frontal) ultrason goruntusunu cekin.</p>
                        <div className={`border-2 ${frontalImage ? 'border-green-300 bg-green-50/30' : 'border-dashed border-slate-200'} rounded-xl p-6 text-center space-y-3`}>
                          <Image className="w-10 h-10 mx-auto text-purple-400" />
                          <p className="text-base font-medium text-slate-700">Frontal Ultrason</p>
                          <p className="text-xs text-slate-500">On yuz goruntuyu cekin</p>
                          {frontalImage ? (
                            <div className="space-y-2">
                              <img src={frontalImage} alt="Frontal" className="w-full max-h-52 object-contain rounded-lg" />
                              <Button size="sm" variant="outline" onClick={() => { setFrontalImage(null); frontalFileRef.current?.click(); }} data-testid="retake-frontal">
                                <RefreshCw className="w-3 h-3 mr-1" /> Tekrar Cek
                              </Button>
                            </div>
                          ) : (
                            <Button onClick={() => frontalFileRef.current?.click()} className="bg-[#5A67D8] hover:bg-[#4F46E5]" size="lg" data-testid="capture-frontal">
                              <Camera className="w-5 h-5 mr-2" /> Fotograf Cek
                            </Button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ADIM 6: 4K URETIM & SONUC */}
                    {analysisStep === 6 && (
                      <div className="space-y-4 mt-2" data-testid="step-generate">
                        {/* Uretim baslamadi — Ozet goster */}
                        {generationStatus === 'idle' && (
                          <div className="space-y-4">
                            <div className="bg-gradient-to-r from-green-50 to-amber-50 rounded-xl p-4 space-y-3">
                              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                <Check className="w-4 h-4 text-green-600" /> Muayene Ozeti
                              </h3>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <span className="text-slate-500">Hasta:</span>
                                  <span className="ml-1 font-medium">{patient?.first_name} {patient?.last_name}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Hafta:</span>
                                  <span className="ml-1 font-medium">{patient?.pregnancy_week || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Cinsiyet:</span>
                                  <span className="ml-1 font-medium">
                                    {biometrics.cinsiyet === 'K' ? 'Kiz' : biometrics.cinsiyet === 'E' ? 'Erkek' : 'Belirsiz'}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-slate-500">Olcum:</span>
                                  <span className="ml-1 font-medium">
                                    {Object.entries(biometrics).filter(([, v]) => v !== '' && v !== undefined).length} parametre
                                  </span>
                                </div>
                              </div>
                              <div className="flex gap-3 mt-2">
                                {profileImage && (
                                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-200 border">
                                    <img src={profileImage} alt="Profil" className="w-full h-full object-cover" />
                                  </div>
                                )}
                                {frontalImage && (
                                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-200 border">
                                    <img src={frontalImage} alt="Frontal" className="w-full h-full object-cover" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Uretim devam ediyor */}
                        {generationStatus === 'generating' && (
                          <div className="flex flex-col items-center justify-center py-8 space-y-4" data-testid="generation-progress">
                            <div className="relative">
                              <div className="w-20 h-20 rounded-full border-4 border-amber-200 border-t-amber-500 animate-spin" />
                              <Sparkles className="w-8 h-8 text-amber-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                            </div>
                            <p className="text-base font-semibold text-slate-700">4K Goruntu Uretiliyor...</p>
                            <p className="text-sm text-slate-500">{generationProgress}</p>
                            <p className="text-xs text-slate-400">Bu islem 1-3 dakika surebilir. Lutfen bekleyin.</p>
                          </div>
                        )}

                        {/* Uretim tamamlandi */}
                        {generationStatus === 'completed' && generatedImage && (
                          <div className="space-y-4" data-testid="generation-result">
                            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                              <Check className="w-5 h-5 text-green-600" />
                              <span className="text-sm font-medium text-green-800">4K goruntu basariyla uretildi!</span>
                            </div>
                            <div className="rounded-xl overflow-hidden border-2 border-green-300 shadow-lg">
                              <img src={generatedImage} alt="4K Sonuc" className="w-full object-contain max-h-[60vh]" />
                            </div>
                            <ShareActions
                              imageBase64={generatedImage}
                              patientPhone={patient?.phone}
                              patientName={`${patient?.first_name || ''} ${patient?.last_name || ''}`}
                              generationType="4k"
                            />
                          </div>
                        )}

                        {/* Uretim basarisiz */}
                        {generationStatus === 'failed' && (
                          <div className="flex flex-col items-center justify-center py-8 space-y-4" data-testid="generation-failed">
                            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                              <Trash2 className="w-8 h-8 text-red-500" />
                            </div>
                            <p className="text-base font-semibold text-red-700">Uretim Basarisiz</p>
                            <p className="text-sm text-slate-500 text-center max-w-md">{generationProgress}</p>
                            <Button
                              onClick={() => { setGenerationStatus('idle'); setGenerationProgress(''); setGenerationJobId(null); setLastScanId(null); }}
                              className="bg-amber-500 hover:bg-amber-600"
                              data-testid="retry-generation"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" /> Tekrar Dene
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <DialogFooter className="mt-4">
                      {analysisStep === 1 && (
                        <>
                          <DialogClose asChild>
                            <Button variant="outline" data-testid="cancel-wizard">Iptal</Button>
                          </DialogClose>
                          <Button onClick={() => setAnalysisStep(2)} className="bg-amber-500 hover:bg-amber-600" disabled={ocrLoading} data-testid="step1-next">
                            Devam <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </>
                      )}
                      {analysisStep === 2 && (
                        <>
                          <Button variant="outline" onClick={() => setAnalysisStep(1)} data-testid="step2-back">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
                          </Button>
                          <Button onClick={() => setAnalysisStep(3)} className="bg-amber-500 hover:bg-amber-600" disabled={ocrLoading} data-testid="step2-next">
                            Devam <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </>
                      )}
                      {analysisStep === 3 && (
                        <>
                          <Button variant="outline" onClick={() => setAnalysisStep(2)} data-testid="step3-back">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => setAnalysisStep(4)}
                            className="text-slate-500 hover:text-slate-700"
                            data-testid="step3-skip"
                          >
                            Atla
                          </Button>
                          <Button onClick={() => setAnalysisStep(4)} className="bg-[#5A67D8] hover:bg-[#4F46E5]" data-testid="step3-next">
                            Devam <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </>
                      )}
                      {analysisStep === 4 && (
                        <>
                          <Button variant="outline" onClick={() => setAnalysisStep(3)} data-testid="step4-back">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
                          </Button>
                          <Button onClick={() => setAnalysisStep(5)} className="bg-[#5A67D8] hover:bg-[#4F46E5]" disabled={!profileImage} data-testid="step4-next">
                            Devam <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </>
                      )}
                      {analysisStep === 5 && (
                        <>
                          <Button variant="outline" onClick={() => setAnalysisStep(4)} data-testid="step5-back">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
                          </Button>
                          <Button onClick={() => { setScanInputType('ultrasound_4k'); setAnalysisStep(6); }} className="bg-[#5A67D8] hover:bg-[#4F46E5]" disabled={!frontalImage} data-testid="step5-next">
                            Devam <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </>
                      )}
                      {analysisStep === 6 && generationStatus === 'idle' && (
                        <>
                          <Button variant="outline" onClick={() => setAnalysisStep(5)} data-testid="step6-back">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
                          </Button>
                          <Button
                            onClick={() => handleCreateScan()}
                            className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                            disabled={submitting || !profileImage || !frontalImage}
                            data-testid="start-4k-generation"
                          >
                            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                            4K Uret
                          </Button>
                        </>
                      )}
                      {analysisStep === 6 && generationStatus === 'generating' && (
                        <Button disabled className="bg-slate-300 cursor-not-allowed">
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uretiliyor...
                        </Button>
                      )}
                      {analysisStep === 6 && (generationStatus === 'completed' || generationStatus === 'failed') && (
                        <DialogClose asChild>
                          <Button className={generationStatus === 'completed' ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-600 hover:bg-slate-700'} data-testid="close-wizard">
                            {generationStatus === 'completed' ? <><Check className="w-4 h-4 mr-1" /> Kapat</> : 'Kapat'}
                          </Button>
                        </DialogClose>
                      )}
                    </DialogFooter>
                    </>)}
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {scansLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#5A67D8]" />
                  </div>
                ) : scans.length === 0 ? (
                  <div className="text-center py-8">
                    <ScanLine className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">Henüz analiz kaydı yok</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {scans.map((scan) => {
                      // Determine navigation path based on engine type
                      let navPath = `/doctor/editor?scanId=${scan.id}`;
                      if (scan.engine === 'ai_face') {
                        navPath = `/doctor/ai-face?scanId=${scan.id}`;
                      } else if (scan.input_type === 'ultrasound_4k') {
                        navPath = `/doctor/ultrasound-4k?scanId=${scan.id}`;
                      }
                      
                      // Determine display info
                      const isAiFace = scan.engine === 'ai_face';
                      const isUltrasound4K = scan.input_type === 'ultrasound_4k';
                      const isUltrasound = scan.input_type === 'ultrasound' && !isAiFace && !isUltrasound4K;
                      
                      return (
                        <div
                          key={scan.id}
                          onClick={() => navigate(navPath)}
                          className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                          data-testid={`scan-item-${scan.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* Thumbnail/badge for completed scans */}
                              {scan.status === 'generated' ? (
                                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0 border border-green-200">
                                  <Sparkles className="w-5 h-5 text-green-600" />
                                </div>
                              ) : isAiFace ? (
                                <ScanLine className="w-5 h-5 text-[#EC4899]" />
                              ) : isUltrasound4K ? (
                                <Sparkles className="w-5 h-5 text-[#F59E0B]" />
                              ) : isUltrasound ? (
                                <Image className="w-5 h-5 text-[#5A67D8]" />
                              ) : (
                                <Activity className="w-5 h-5 text-[#5A67D8]" />
                              )}
                              <div>
                                <p className="font-medium text-slate-800">
                                  {isAiFace 
                                    ? 'AI Yüz Analizi' 
                                    : isUltrasound4K
                                      ? 'Ultrason 4K'
                                      : isUltrasound 
                                        ? 'Ultrason Analizi' 
                                        : '3D Model Analizi'}
                                </p>
                                <div className="flex items-center gap-3 text-xs text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {new Date(scan.updated_at || scan.created_at).toLocaleString('tr-TR')}
                                  </span>
                                  {scan.metadata?.week && (
                                    <span className="text-slate-400">
                                      {scan.metadata.week}. hafta
                                    </span>
                                  )}
                                  {scan.metadata?.gender && (
                                    <span className={scan.metadata.gender === 'female' ? 'text-pink-500' : 'text-blue-500'}>
                                      {scan.metadata.gender === 'female' ? '♀ Kız' : '♂ Erkek'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {getStatusBadge(scan.status)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PatientDetailPage;
